import { MagnifyingGlassIcon, FunnelIcon, EyeIcon, ArrowDownTrayIcon, LinkIcon, DocumentIcon, DocumentTextIcon, ChevronUpDownIcon, BuildingOffice2Icon, UserIcon, TrashIcon, CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { DocumentIcon as DocumentIconSolid, DocumentTextIcon as DocumentTextIconSolid } from "@heroicons/react/24/solid";
import Pagination from "components/common/Pagination";
import DocumentPreviewModal from "components/preview/DocumentPreviewModal";
import { Tooltip } from "components/common/Tooltip";
import ShareContractModal from "./ShareContractModal";
import ConfirmDeleteModal from "components/common/ConfirmDeleteModal";
import { useContracts } from "hooks/useContracts";
import { useState, useEffect, useMemo } from "react";
import { Contract } from "types/contract";
import { useLanguage } from "contexts/LanguageContext";
import { useCustomers } from "hooks/useCustomers";
import { Combobox } from "@headlessui/react";
import { useStorage } from "hooks/useStorage";
import toast from "react-hot-toast";
import { useOrganizations } from "hooks/useOrganizations";

interface ContractListsProps {
  // No props needed anymore
}

const ContractLists = ({}: ContractListsProps) => {
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState<"" | "contract" | "quotation" | "other">("");
  const [customerFilter, setCustomerFilter] = useState<string>("");
  const [customerQuery, setCustomerQuery] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { contracts, updateContract, refreshContracts, deleteContract } = useContracts();
  const { getCustomerById, customers } = useCustomers();
  const { deleteFile } = useStorage();
  const { organizations } = useOrganizations();
  const [selectedContracts, setSelectedContracts] = useState<string[]>([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Dokumentum előnézet modal kezelése
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewContract, setPreviewContract] = useState<Contract | null>(null);

  // Megosztás modal kezelése
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [shareContract, setShareContract] = useState<Contract | null>(null);

  const [customerNames, setCustomerNames] = useState<Record<string, string>>({});

  // Ügyfél nevek betöltése
  useEffect(() => {
    const loadCustomerNames = async () => {
      const names: Record<string, string> = {};
      for (const contract of contracts) {
        if (contract.customerId) {
          const customer = await getCustomerById(contract.customerId);
          if (customer) {
            names[contract.customerId] = customer.type === 'organization'
              ? customer.companyName || ''
              : `${customer.firstName} ${customer.lastName}`;
          }
        }
      }
      setCustomerNames(names);
    };
    loadCustomerNames();
  }, [contracts]);

  // Reset selections when filters change
  useEffect(() => {
    setSelectedContracts([]);
  }, [searchTerm, typeFilter, customerFilter]);

  const filteredCustomers = useMemo(() => {
    return customers.filter(customer => {
      const searchStr = customer.type === 'organization'
        ? customer.companyName?.toLowerCase()
        : `${customer.firstName} ${customer.lastName}`.toLowerCase();
      
      const searchTermLower = customerQuery.toLowerCase();

      return searchStr?.includes(searchTermLower) ||
        // Email keresés
        customer.email?.toLowerCase().includes(searchTermLower) ||
        // Adószám és cégjegyzékszám keresés
        customer.taxNumber?.toLowerCase().includes(searchTermLower) ||
        customer.registrationNumber?.toLowerCase().includes(searchTermLower) ||
        // Cím elemek keresése
        customer.address?.street?.toLowerCase().includes(searchTermLower) ||
        customer.address?.city?.toLowerCase().includes(searchTermLower) ||
        customer.address?.postalCode?.toLowerCase().includes(searchTermLower) ||
        customer.address?.country?.toLowerCase().includes(searchTermLower) ||
        // Teljes cím keresése (ha van)
        customer.companyAddress?.toLowerCase().includes(searchTermLower);
    });
  }, [customers, customerQuery]);

  const filteredContracts = contracts.filter(contract => {
    const matchesSearch = contract.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === "" || contract.type === typeFilter;
    const matchesCustomer = customerFilter === "" || contract.customerId === customerFilter;

    return matchesSearch && matchesType && matchesCustomer;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredContracts.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentContracts = filteredContracts.slice(indexOfFirstItem, indexOfLastItem);
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  // Státusz változtatás kezelése
  const handleStatusChange = async (contractId: string, currentStatus: Contract["status"]) => {
    try {
      // Státusz körforgás: valid -> expired -> draft -> valid
      let newStatus: Contract["status"];

      if (currentStatus === "valid") {
        newStatus = "expired";
      } else if (currentStatus === "expired") {
        newStatus = "draft";
      } else {
        // draft vagy bármi más esetén
        newStatus = "valid";
      }

      // Frissítés az adatbázisban (ez most már a helyi állapotot is frissíti)
      await updateContract(contractId, { status: newStatus });
    } catch (error) {
      console.error("Hiba a státusz változtatása során:", error);
    }
  };

  const getStatusBadge = (status: Contract["status"], contractId: string) => {
    // Egyszerűsített státusz megjelenítés
    if (!status || (status !== "valid" && status !== "expired" && status !== "draft")) {
      // Ha a státusz nem megfelelő, alapértelmezetten érvényes állapot
      return (
        <span
          className="bg-green-500 text-white text-xs px-2 py-1 rounded-full cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => handleStatusChange(contractId, "valid")}
        >
          {t("contracts.status.valid")}
        </span>
      );
    }

    if (status === "valid") {
      return (
        <Tooltip content={t("contracts.setAsExpired")}>
          <span
            className="bg-green-500 text-white text-xs px-2 py-1 rounded-full cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => handleStatusChange(contractId, status)}
          >
            {t("contracts.status.valid")}
          </span>
        </Tooltip>
      );
    } else if (status === "expired") {
      return (
        <Tooltip content={t("contracts.setAsDraft")}>
          <span
            className="bg-red-500 text-white text-xs px-2 py-1 rounded-full cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => handleStatusChange(contractId, status)}
          >
            {t("contracts.status.expired")}
          </span>
        </Tooltip>
      );
    } else {
      // Draft státusz
      return (
        <Tooltip content={t("contracts.setAsValid")}>
          <span
            className="bg-gray-500 text-white text-xs px-2 py-1 rounded-full cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => handleStatusChange(contractId, status)}
          >
            {t("contracts.status.draft")}
          </span>
        </Tooltip>
      );
    }
  };

  const getTypeIcon = (type: Contract["type"]) => {
    switch (type) {
      case "contract":
        return <DocumentTextIcon className="h-5 w-5 text-primary-400" />;
      case "quotation":
        return <DocumentIcon className="h-5 w-5 text-amber-400" />;
      case "other":
        return <DocumentIcon className="h-5 w-5 text-gray-400" />;
      default:
        return <DocumentIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  const getTypeName = (type: Contract["type"]) => {
    switch (type) {
      case "contract":
        return t("contracts.contract");
      case "quotation":
        return t("contracts.quotation");
      case "other":
        return t("contracts.other");
      default:
        return type;
    }
  };

  // Dokumentum előnézet kezelése
  const handlePreview = (contract: Contract, e: React.MouseEvent) => {
    e.stopPropagation();
    setPreviewContract(contract);
    setIsPreviewOpen(true);
  };

  // Megosztás kezelése
  const handleShare = (contract: Contract, e: React.MouseEvent) => {
    e.stopPropagation();
    setShareContract(contract);
    setIsShareModalOpen(true);
  };

  // Letöltés kezelése
  const handleDownload = (contract: Contract, e: React.MouseEvent) => {
    e.stopPropagation();
    if (contract.fileUrl) {
      // Létrehozunk egy ideiglenes linket a letöltéshez
      const link = document.createElement('a');
      link.href = contract.fileUrl;
      link.download = contract.fileName || contract.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Törlés kezelése
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [contractToDelete, setContractToDelete] = useState<Contract | null>(null);
  const [contractsToDelete, setContractsToDelete] = useState<Contract[]>([]);

  const handleDelete = async (contract: Contract, e: React.MouseEvent) => {
    e.stopPropagation();
    setContractToDelete(contract);
    setContractsToDelete([contract]);
    setIsDeleteModalOpen(true);
  };

  const handleBulkDelete = () => {
    const contractsToDelete = filteredContracts.filter(c => selectedContracts.includes(c.id));
    setContractsToDelete(contractsToDelete);
    setContractToDelete(null);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (contractsToDelete.length > 0) {
      try {
        for (const contract of contractsToDelete) {
          // Töröljük a fájlokat a storage-ból, ha léteznek
          if (contract.fileUrl) {
            try {
              const filePath = contract.fileUrl.split('/o/')[1].split('?')[0];
              await deleteFile(decodeURIComponent(filePath));
            } catch (error) {
              console.warn("A fájl nem található a storage-ban:", error);
              // Folytatjuk a törlést, még ha a fájl nem is létezik
            }
          }

          // Töröljük a szerződést az adatbázisból
          await deleteContract(contract.id);
        }

        // Frissítjük a szerződések listáját
        await refreshContracts();

        // Clear selected contracts
        setSelectedContracts([]);

        const message = contractsToDelete.length > 1 
          ? t("contracts.messages.bulkDeleted", { count: contractsToDelete.length })
          : t("contracts.messages.deleted");

        toast.success(message);
      } catch (error) {
        console.error("Hiba a szerződés(ek) törlése során:", error);
        toast.error(t("contracts.messages.deleteError"));
      } finally {
        setIsDeleteModalOpen(false);
        setContractToDelete(null);
        setContractsToDelete([]);
      }
    }
  };

  // Selected contracts logic
  const allVisible = filteredContracts.length > 0;
  const allSelected = allVisible && filteredContracts.every(c => selectedContracts.includes(c.id));
  const someSelected = selectedContracts.length > 0 && !allSelected;

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedContracts([]);
    } else {
      setSelectedContracts(filteredContracts.map(c => c.id));
    }
  };

  const handleSelectContract = (id: string, isSelected: boolean) => {
    if (isSelected) {
      setSelectedContracts(prev => [...prev, id]);
    } else {
      setSelectedContracts(prev => prev.filter(cId => cId !== id));
    }
  };

  // Segédfüggvény a szervezet nevének megjelenítéséhez
  const getOrganizationName = (organizationId?: string) => {
    if (!organizationId) return "-";
    const org = organizations.find(org => org.id === organizationId);
    return org ? org.name : "-";
  };

  // Fájl formátum azonosítása
  const getFileFormat = (contract: Contract) => {
    if (!contract.fileUrl) return null;
    
    const fileName = contract.fileName || contract.fileUrl;
    if (fileName.toLowerCase().endsWith('.pdf')) {
      return 'pdf';
    } else if (fileName.toLowerCase().endsWith('.docx') || fileName.toLowerCase().endsWith('.doc')) {
      return 'docx';
    }
    return null;
  };

  // Formátum badge megjelenítése
  const getFormatBadge = (format: string | null) => {
    if (!format) return null;
    
    switch(format) {
      case 'pdf':
        return (
          <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            PDF
          </span>
        );
      case 'docx':
        return (
          <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            DOCX
          </span>
        );
      default:
        return null;
    }
  };

  // Formátum ikon megjelenítése
  const getFormatIcon = (format: string | null) => {
    if (!format) return <DocumentIcon className="h-5 w-5 text-gray-400" />;
    
    switch(format) {
      case 'pdf':
        return <DocumentIconSolid className="h-5 w-5 text-red-500" />;
      case 'docx':
        return <DocumentTextIconSolid className="h-5 w-5 text-blue-500" />;
      default:
        return <DocumentIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
        {selectedContracts.length > 0 && (
          <div className="bg-primary-50 border-b border-primary-100 p-2 flex items-center justify-between">
            <div className="flex items-center">
              <span className="text-primary-700 text-sm font-medium mr-2">
                {selectedContracts.length} {t("contracts.selected")}
              </span>
            </div>
            <button
              onClick={handleBulkDelete}
              className="inline-flex items-center px-2.5 py-1.5 bg-red-50 border border-red-200 rounded-md text-red-600 text-xs font-medium hover:bg-red-100 transition-colors"
            >
              <TrashIcon className="h-3.5 w-3.5 mr-1" />
              {t("contracts.bulkDelete")}
            </button>
          </div>
        )}
        
        <div className="flex flex-wrap items-center justify-between p-3 border-b border-gray-200">
          <div className="flex flex-col w-full gap-3">
            {/* Szűrők mobil nézetben */}
            <div className="flex w-full items-center gap-2">
              <div className="relative flex-grow">
                <input
                  type="text"
                  placeholder={t("contracts.placeholder.search")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-white border border-gray-300 rounded-md py-2 pl-3 pr-8 text-sm text-gray-700 focus:ring-1 focus:ring-primary-500"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-500" />
                </div>
              </div>
              <button
                className="flex-shrink-0 bg-primary-50 p-2 rounded-md text-primary-600 hover:bg-primary-100 border border-primary-200 flex items-center gap-1"
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                aria-label={isFilterOpen ? t("contracts.filter.hideFilters") : t("contracts.filter.showFilters")}
              >
                <FunnelIcon className={`h-5 w-5 ${isFilterOpen ? 'text-primary-600' : 'text-primary-500'}`} />
                <span className="hidden xs:inline text-xs font-medium">
                  {isFilterOpen ? t("contracts.filter.hideFilters") : t("contracts.filter.showFilters")}
                </span>
              </button>
            </div>

            {/* Mobil és asztali közös szűrők - always shown on mobile for better UX */}
            <div className={`w-full flex flex-wrap gap-3 ${isFilterOpen ? 'block' : 'hidden sm:flex'}`}>
                {/* Típus szűrők */}
              <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                <button
                  onClick={() => setTypeFilter("")}
                  className={`inline-flex items-center px-3 py-2 rounded-md border text-xs font-medium
                    ${typeFilter === ""
                      ? 'border-teal-500 bg-teal-500/20 text-teal-700'
                      : 'border-gray-300 text-gray-600 hover:border-gray-400 bg-white'
                    } flex-1 sm:flex-none justify-center`}
                >
                  <DocumentIcon className="w-3.5 h-3.5 mr-1.5" />
                  {t("contracts.filter.allTypes")}
                </button>
                <button
                  onClick={() => setTypeFilter("contract")}
                  className={`inline-flex items-center px-3 py-2 rounded-md border text-xs font-medium
                    ${typeFilter === "contract"
                      ? 'border-blue-500 bg-blue-500/20 text-blue-700'
                      : 'border-gray-300 text-gray-600 hover:border-gray-400 bg-white'
                    } flex-1 sm:flex-none justify-center`}
                >
                  <DocumentTextIcon className="w-3.5 h-3.5 mr-1.5 text-blue-600" />
                  {t("contracts.filter.contract")}
                </button>
                <button
                  onClick={() => setTypeFilter("quotation")}
                  className={`inline-flex items-center px-3 py-2 rounded-md border text-xs font-medium
                    ${typeFilter === "quotation"
                      ? 'border-amber-500 bg-amber-500/20 text-amber-700'
                      : 'border-gray-300 text-gray-600 hover:border-gray-400 bg-white'
                    } flex-1 sm:flex-none justify-center`}
                >
                  <DocumentIcon className="w-3.5 h-3.5 mr-1.5 text-amber-600" />
                  {t("contracts.filter.quotation")}
                </button>
                <button
                  onClick={() => setTypeFilter("other")}
                  className={`inline-flex items-center px-3 py-2 rounded-md border text-xs font-medium
                    ${typeFilter === "other"
                      ? 'border-purple-500 bg-purple-500/20 text-purple-700'
                      : 'border-gray-300 text-gray-600 hover:border-gray-400 bg-white'
                    } flex-1 sm:flex-none justify-center`}
                >
                  <DocumentIcon className="w-3.5 h-3.5 mr-1.5 text-purple-600" />
                  {t("contracts.filter.other")}
                </button>
              </div>

              {/* Customer filter - combobox */}
              <div className="relative z-10 w-full sm:w-auto">
                <Combobox value={customerFilter} onChange={setCustomerFilter}>
              <div className="relative">
                    <div className={`bg-white relative rounded-md border ${customerFilter ? 'border-indigo-500 ring-1 ring-indigo-500' : 'border-gray-300'}`}>
                      <Combobox.Input
                        as="div"
                        className="w-full cursor-pointer"
                      >
                        <div 
                          className="flex items-center px-3 py-2 text-xs text-left min-h-[40px]"
                          onClick={() => setCustomerQuery("")}
              >
                          <UserIcon className="w-4 h-4 mr-2 text-gray-500 flex-shrink-0" />
                          <span className="truncate flex-1 font-medium">
                            {customerFilter
                              ? customerNames[customerFilter] || t("contracts.filter.unknownCustomer")
                              : t("contracts.filter.allCustomers")}
                          </span>
                          <ChevronUpDownIcon className="h-5 w-5 text-gray-400 flex-shrink-0" aria-hidden="true" />
            </div>
                      </Combobox.Input>
          </div>
                    <Combobox.Options className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg border border-gray-200 focus:outline-none">
                      <div className="p-3 border-b border-gray-100">
                        <input
                          type="text"
                          value={customerQuery}
                          onChange={(e) => setCustomerQuery(e.target.value)}
                          placeholder={t("contracts.filter.searchCustomer")}
                          className="w-full border border-gray-300 rounded-md py-2 px-3 text-sm"
                        />
        </div>
                      <Combobox.Option
                        key="all"
                        value=""
                        className={({ active }) =>
                          `relative cursor-pointer select-none py-2 pl-8 pr-4 ${
                            active ? 'bg-primary-100 text-primary-600' : 'text-gray-700'
                          }`
                        }
                      >
                        {({ selected }) => (
                          <>
                            <span className="block truncate font-medium">
                {t("contracts.filter.allCustomers")}
                            </span>
                            {selected && (
                              <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-primary-600">
                                <CheckIcon className="h-4 w-4" aria-hidden="true" />
                              </span>
                            )}
                          </>
                        )}
                      </Combobox.Option>
                      {filteredCustomers.map((customer) => (
                        <Combobox.Option
                  key={customer.id}
                          value={customer.id}
                          className={({ active }) =>
                            `relative cursor-pointer select-none py-2 pl-8 pr-4 ${
                              active ? 'bg-primary-100 text-primary-600' : 'text-gray-700'
                            }`
                          }
                >
                          {({ selected }) => (
                            <>
                              <span className="block truncate">
                  {customer.type === 'organization' 
                                  ? customer.companyName || ''
                      : `${customer.firstName} ${customer.lastName}`}
                  </span>
                              {selected && (
                                <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-primary-600">
                                  <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                </span>
                              )}
                            </>
                          )}
                        </Combobox.Option>
                      ))}
                    </Combobox.Options>
                </div>
                </Combobox>
              </div>
            </div>
          </div>
        </div>

        {/* ASZTALI ÉS TABLET NÉZET - TÁBLÁZAT */}
        <div className="hidden lg:block overflow-x-auto">
          <div className="w-full overflow-x-auto">
            <table className="w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-2 py-2 w-8">
                    <div className="flex items-center justify-center">
                      <div 
                        className={`w-4 h-4 border rounded flex items-center justify-center cursor-pointer
                          ${allSelected 
                            ? 'bg-primary-500 border-primary-500 text-white' 
                            : someSelected 
                              ? 'bg-primary-100 border-primary-300' 
                              : 'border-gray-300'
                          }`}
                        onClick={handleSelectAll}
                      >
                        {allSelected && <CheckIcon className="w-3 h-3" />}
                        {someSelected && <div className="w-2 h-2 bg-primary-500 rounded-sm"></div>}
                      </div>
                    </div>
                  </th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("contracts.fields.type")}
                  </th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                    {t("contracts.fields.name")}
                  </th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    {t("contracts.fields.customer")}
                  </th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("contracts.fields.organization")}
                  </th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    {t("contracts.fields.validStart")}
                  </th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    {t("contracts.fields.validEnd")}
                  </th>
                  <th scope="col" className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("contracts.fields.status")}
                  </th>
                  <th scope="col" className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("contracts.fields.actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentContracts.map((contract) => (
                  <tr key={contract.id} className="hover:bg-gray-50">
                    <td className="px-2 py-2">
                      <div className="flex items-center justify-center">
                        <div 
                          className={`w-4 h-4 border rounded flex items-center justify-center cursor-pointer
                            ${selectedContracts.includes(contract.id) 
                              ? 'bg-primary-500 border-primary-500 text-white' 
                              : 'border-gray-300 hover:border-gray-400'
                            }`}
                          onClick={() => handleSelectContract(contract.id, !selectedContracts.includes(contract.id))}
                        >
                          {selectedContracts.includes(contract.id) && <CheckIcon className="w-3 h-3" />}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center">
                        <div className="p-1.5 rounded bg-gradient-to-br from-teal-500/20 to-teal-600/20 border-teal-500/20 border mr-2 flex-shrink-0">
                          {getFileFormat(contract) ? getFormatIcon(getFileFormat(contract)) : getTypeIcon(contract.type)}
                        </div>
                        <div className="min-w-0 w-full">
                          <div className="text-sm font-medium text-gray-800 max-w-none flex items-center whitespace-normal break-words">
                              {contract.name}
                            {getFormatBadge(getFileFormat(contract))}
                            </div>
                          <div className="text-xs text-gray-500">
                            {t("contracts.types." + contract.type)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-sm text-gray-800">
                        {contract.customerId ? customerNames[contract.customerId] || "-" : "-"}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center">
                        {contract.organizationId && (
                          <div className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium bg-blue-500/10 text-blue-600 border border-blue-500/20">
                            <BuildingOffice2Icon className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5 sm:mr-1 flex-shrink-0" />
                            <span className="truncate">{getOrganizationName(contract.organizationId)}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-sm text-gray-800">
                        {contract.createdAt.toLocaleDateString("hu-HU")}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-sm text-gray-800">
                        {contract.validStart ? contract.validStart.toLocaleDateString("hu-HU") : "-"}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-sm text-gray-800">
                        {contract.validEnd ? contract.validEnd.toLocaleDateString("hu-HU") : "-"}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex justify-center">
                        {getStatusBadge(contract.status, contract.id)}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex justify-center space-x-2">
                        <Tooltip content={t("contracts.actions.view")}>
                          <button
                            onClick={(e) => handlePreview(contract, e)}
                            className="text-gray-500 hover:text-primary-600"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                        </Tooltip>
                        <Tooltip content={t("contracts.actions.download")}>
                          <button
                            className="text-gray-500 hover:text-primary-600"
                            onClick={(e) => handleDownload(contract, e)}
                          >
                            <ArrowDownTrayIcon className="h-4 w-4" />
                          </button>
                        </Tooltip>
                        <Tooltip content={t("contracts.actions.share")}>
                          <button
                            onClick={(e) => handleShare(contract, e)}
                            className="text-gray-500 hover:text-primary-600"
                          >
                            <LinkIcon className="h-4 w-4" />
                          </button>
                        </Tooltip>
                        <Tooltip content={t("contracts.actions.delete")}>
                          <button
                            onClick={(e) => handleDelete(contract, e)}
                            className="text-gray-500 hover:text-red-600"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </Tooltip>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* MOBIL NÉZET - KÁRTYÁK */}
        <div className="lg:hidden space-y-4 mb-4 w-full overflow-hidden">
          {currentContracts.map((contract) => (
            <div key={contract.id} className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              {/* Card header with select checkbox, type icon, and name */}
              <div 
                className="flex items-center p-4 border-b border-gray-200 bg-gray-50"
                onClick={() => handleSelectContract(contract.id, !selectedContracts.includes(contract.id))}
              >
                  <div 
                  className={`w-6 h-6 border rounded-md flex items-center justify-center cursor-pointer mr-3
                      ${selectedContracts.includes(contract.id) 
                        ? 'bg-primary-500 border-primary-500 text-white' 
                        : 'border-gray-300'
                      }`}
                  >
                  {selectedContracts.includes(contract.id) && <CheckIcon className="w-4 h-4" />}
                  </div>
                <div className="p-2 rounded-md bg-gradient-to-br from-teal-500/20 to-teal-600/20 border-teal-500/20 border mr-3 flex-shrink-0">
                  {getFileFormat(contract) ? getFormatIcon(getFileFormat(contract)) : getTypeIcon(contract.type)}
                  </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-800 flex items-center line-clamp-1 mb-1">
                        {contract.name}
                    <span className="ml-2">{getFormatBadge(getFileFormat(contract))}</span>
                      </div>
                  <div className="text-xs text-gray-500">
                      {t("contracts.types." + contract.type)}
                    </div>
                  </div>
                <div className="ml-2 flex-shrink-0">
                  {getStatusBadge(contract.status, contract.id)}
                </div>
              </div>

              {/* Card content */}
              <div className="p-4">
                {/* Customer and organization section */}
                <div className="flex flex-col gap-3 mb-4">
                  <div className="flex items-start">
                    <div className="bg-gray-100 p-1 rounded-md mr-2 flex-shrink-0">
                      <UserIcon className="w-4 h-4 text-gray-500" />
                </div>
                    <div>
                      <span className="text-xs font-medium text-gray-700 block mb-0.5">{t("contracts.fields.customer")}</span>
                      <span className="text-sm text-gray-800 truncate block">
                        {contract.customerId ? customerNames[contract.customerId] || "-" : "-"}
                      </span>
                  </div>
              </div>

                  {contract.organizationId && (
                    <div className="flex items-start">
                      <div className="bg-gray-100 p-1 rounded-md mr-2 flex-shrink-0">
                        <BuildingOffice2Icon className="w-4 h-4 text-gray-500" />
                </div>
                      <div>
                        <span className="text-xs font-medium text-gray-700 block mb-0.5">{t("contracts.fields.organization")}</span>
                        <span className="text-sm text-gray-800 truncate block">
                          {getOrganizationName(contract.organizationId)}
                        </span>
                      </div>
                    </div>
                  )}
              </div>

                {/* Date section */}
                <div className="grid grid-cols-2 gap-4 mb-3 border-t border-gray-100 pt-3">
                  <div>
                    <span className="text-xs font-medium text-gray-600 block mb-1">{t("contracts.fields.validStart")}</span>
                    <span className="text-sm font-medium text-gray-800 block">
                      {contract.validStart ? contract.validStart.toLocaleDateString("hu-HU") : "-"}
                    </span>
                </div>
                  <div>
                    <span className="text-xs font-medium text-gray-600 block mb-1">{t("contracts.fields.validEnd")}</span>
                    <span className="text-sm font-medium text-gray-800 block">
                      {contract.validEnd ? contract.validEnd.toLocaleDateString("hu-HU") : "-"}
                    </span>
                </div>
              </div>

                {/* View count if available */}
              {contract.shareSettings?.viewCount !== undefined && (
                  <div className="flex items-center text-xs text-gray-500 mb-1">
                    <EyeIcon className="h-4 w-4 mr-1.5 flex-shrink-0" />
                  <span>{contract.shareSettings.viewCount} {t("contracts.fields.views")}</span>
                </div>
              )}
              </div>

              {/* Action buttons */}
              <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 p-2">
                <button
                  onClick={(e) => handlePreview(contract, e)}
                  className="flex items-center justify-center text-xs px-3 py-2 rounded-md text-gray-700 hover:text-primary-600 hover:bg-primary-50 flex-1 mx-1"
                >
                  <EyeIcon className="h-4 w-4 mr-1.5" />
                  <span>{t("contracts.actions.view")}</span>
                </button>
                <button
                  onClick={(e) => handleDownload(contract, e)}
                  className="flex items-center justify-center text-xs px-3 py-2 rounded-md text-gray-700 hover:text-primary-600 hover:bg-primary-50 flex-1 mx-1"
                >
                  <ArrowDownTrayIcon className="h-4 w-4 mr-1.5" />
                  <span className="hidden xs:inline">{t("contracts.actions.download")}</span>
                </button>
                <button
                  onClick={(e) => handleShare(contract, e)}
                  className="flex items-center justify-center text-xs px-3 py-2 rounded-md text-gray-700 hover:text-primary-600 hover:bg-primary-50 flex-1 mx-1"
                >
                  <LinkIcon className="h-4 w-4 mr-1.5" />
                  <span className="hidden xs:inline">{t("contracts.actions.share")}</span>
                </button>
                <button
                  onClick={(e) => handleDelete(contract, e)}
                  className="flex items-center justify-center text-xs px-3 py-2 rounded-md text-gray-700 hover:text-red-600 hover:bg-red-50 flex-1 mx-1"
                >
                  <TrashIcon className="h-4 w-4 mr-1.5" />
                  <span className="hidden xs:inline">{t("contracts.actions.delete")}</span>
                </button>
              </div>
            </div>
          ))}
        </div>

        {filteredContracts.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            itemsPerPage={itemsPerPage}
            totalItems={filteredContracts.length}
            indexOfFirstItem={indexOfFirstItem}
            indexOfLastItem={indexOfLastItem}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        )}

        {/* Empty state */}
        {filteredContracts.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 px-4 border-t border-gray-200">
            <div className="p-4 rounded-full bg-gray-100 mb-4">
              <DocumentTextIcon className="h-12 w-12 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-700 mb-2">
              {t("contracts.noContracts")}
            </h3>
            <p className="text-sm text-gray-500 text-center max-w-md mb-6">
              {searchTerm || typeFilter || customerFilter 
                ? t("contracts.filterNoResults") || "No documents match your current filters. Try adjusting your search criteria."
                : t("contracts.emptyState") || "You haven't created any documents yet. Get started by creating your first document."}
            </p>
            
            {(searchTerm || typeFilter || customerFilter) && (
              <div className="flex flex-wrap gap-2 justify-center">
                {searchTerm && (
                  <button 
                    onClick={() => setSearchTerm("")}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <XMarkIcon className="h-4 w-4 mr-1.5" />
                    {t("contracts.clearSearch") || "Clear search"}
                  </button>
                )}
                {typeFilter && (
                  <button 
                    onClick={() => setTypeFilter("")}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <XMarkIcon className="h-4 w-4 mr-1.5" />
                    {t("contracts.clearTypeFilter") || "Clear type filter"}
                  </button>
                )}
                {customerFilter && (
                  <button 
                    onClick={() => setCustomerFilter("")}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <XMarkIcon className="h-4 w-4 mr-1.5" />
                    {t("contracts.clearCustomerFilter") || "Clear customer filter"}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Dokumentum előnézet modal */}
      <DocumentPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        contract={previewContract}
      />

      {/* Megosztás modal */}
      <ShareContractModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        contract={shareContract}
      />

      {/* Törlés megerősítő modal */}
      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setContractToDelete(null);
          setContractsToDelete([]);
        }}
        onConfirm={handleConfirmDelete}
        title={contractsToDelete.length > 1 ? t("contracts.bulkDeleteTitle") : t("contracts.deleteTitle")}
        deleteMessage={
          contractsToDelete.length > 1 
            ? t("contracts.bulkDeleteMessage", { count: contractsToDelete.length }) 
            : t("contracts.deleteMessage", { name: contractToDelete?.name || t("contracts.unknown") })
        }
        deleteWarning={contractsToDelete.length > 1 ? t("contracts.bulkDeleteWarning") : t("contracts.deleteWarning")}
      />
    </>
  );
};

export default ContractLists;

